// src/com/auto_dida_info/AutoDidaInfoUploader.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    DialogContent, Button, DialogActions, Chip
} from '@mui/material';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // upload file
import DoneIcon from '@mui/icons-material/Done'; // close the dialog.
import FindInPageIcon from '@mui/icons-material/FindInPage'; // search file
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant'; // err
import { useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { AppWord, AppStyle } from '../../value/AppConst';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import DraggableEditor from '../../common/DraggableEditor';
import { userInfoRepo, authTableTreeRepo } from '../../value/AppModel';
import { usePapaParse } from 'react-papaparse';
import { AutoDidaInfoDictionary } from './AutoDidaInfoDict';
import { UPLOAD_AUTO_DIDA_INFO } from './AutoDidaInfoGql'; // 여기서 올리고 & 반응을 보여줘야 함.
import IsValid from '../../value/IsValid';
import FlexyTable from '../../common/flexytable/FlexyTable';
import InputUtil from '../../value/InputUtil';

const HeaderLine = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 10, boxSizing: 'border-box',
});

const tableProps = AutoDidaInfoDictionary.uploadTableProps;
// extract for upload from InputMap
const { dcCd, storeId, didaDate } = AutoDidaInfoDictionary.inputMap;
const uploadMap = {
    dcCd, storeId, didaDate
};
const EmptyCsvData = {data:[], errors:[], meta:{}};

function checkItem(item) {
    const errors = [];
    for(const id of Object.keys(uploadMap)) {
        const label = uploadMap[id].label;
        if(IsValid.nullString(item[id])) {
            if(uploadMap[id].required && uploadMap[id].toAdd) {
                errors.push(`${label} 없음.`);
            }
        }
        else {
            const check = uploadMap[id].check;
            if(check) {
                const good = check(item[id]);
                if(!good) {
                    errors.push(`${label} 오류.`);
                }
            }
        }
    }
    if(errors.length > 0) {
        item.ok = false;
        item.message = errors.join("\n");
    }
    else {
        item.ok = true;
        item.message = 'Ready';
    }
    return item;
}

// replace someKey with proper one
function checkAndMap(dcCd, data) {
    let countError = 0;
    const records
    = data
    .filter((array) => /^\d{3}$/.test(array[0]))
    .map((array, idx) => {
        let item = {
            key: `${array[0]}_${array[1]}_${array[2]}`,
            dcCd: array[0],
            storeId: array[1],
            didaDate: array[2],
            // check columns and adjust numbers
        }
        if(item.dcCd !== dcCd) {
            item.ok = false;
            item.message = 'DC 불일치';
        }
        else if(!IsValid.date(item.didaDate)) {
            item.ok = false;
            item.message = '날짜형식 오류';
        }
        else item = checkItem(item);
        if(!item.ok) countError++;
        return item;
    });
    return {records, countError};
}

export default function AutoDidaInfoUploader({
    open,
    dc,
    csvText,
    isEucKr,
    onClose,
}) {
    const [text, setText] = useState('');
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [csvData, setCsvData] = useState(EmptyCsvData); // {data:[], errors:[], meta:{}}
    const [tableData, setTableData] = useState([]);
    const [errorCount, setErrorCount] = useState(0);
    const [changed, setChanged] = useState(false);
    const [msgDone, setMsgDone] = useState('');
    const [useEucKr, setUseEucKr] = useState(false);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const checkAuth = useAuthorizationCheck();
    const fileInputRef = useRef(null);
    const { readString } = usePapaParse();
    const wellUploadCountRef = useRef(0);

    const [uploadAutoDidaInfo, responseUpload] = useMutation(UPLOAD_AUTO_DIDA_INFO, {
        onCompleted: (data, option) => {onCompletedUpload(data, option);},
        onError: (error) => {
            addAlert({title: "점포 휴무일 정보 업로드 오류", message: [error.message]});
        }
    });

    useEffect(()=>{
        if(open) {
            setText(csvText);
            setUseEucKr(isEucKr);
        }
        else {
            setText('');
            setCsvData(EmptyCsvData);
        }
    }, [open, csvText, isEucKr]);

    // 내부에서 다시 파일 선택할 수 있으므로 csvText에서 직접 처리하지 않고 csvText => text => csvData로 처리.
    useEffect(()=>{
        if(!IsValid.nullString(text)) {
            readString(text, {
                worker: true,
                complete: (result) => {
                    const { data, errors, meta } = result;
                    data.shift(); // remove header.
                    const records
                    = data // .filter((row) => /^\d{3}/.test(row[0]))
                    .map((row) => {
                        const newRow = row.slice(0, Object.keys(uploadMap).length); // 사용할 칼럼 수만큼 자른다. 확인!!
                        // 추가작업 필요하면 여기에. (데이터 변경, 계산 등)

                        return newRow;
                    });
                    setCsvData({data: records, errors, meta});
                }
            })
        }
    }, [text, readString]);

    useEffect(()=>{
        if(open && dc) {
            const {records, countError} = checkAndMap(dc.dcCd, csvData.data);
            setTableData(records);
            setMsgDone('');
            setErrorCount(countError);
            setChanged(true);
        }
    }, [csvData, sessionInfo, dc, open]);

    const onCompletedUpload = (data, option) => {
        if(data.autoDidaInfoUpload.ok) {
            // reflect the result to the table.(ok, message, list {ok, message, arriveId})
            const newTableData = [];
            for(const prev of tableData) {
                const found = data.autoDidaInfoUpload.list.find((item)=>prev.key === `${item.dcCd}_${item.storeId}_${item.didaDate}`);
                if(found) {
                    prev.ok = found.ok;
                    prev.message = found.message;
                    newTableData.push(prev);
                    if(found.ok) wellUploadCountRef.current++;
                }
                else newTableData.push(prev);
            }
            setTableData(newTableData);
            setChanged(false); // 다시 올리지 않도록.
            enqueueSnackbar("점포 휴무일 정보 업로드 완료", {variant: 'success'});
            setMsgDone('[업로드 완료] ');
        }
        else addAlert({title: "점포 휴무일 정보 업로드", message: [data.autoDidaInfoUpload.message]});
    };

    const onClickUpload = () => {
        // 아래 filter에서 "row &&" 빼면 오류 발생함. row가 널이거나 undefined인 경우가 없는데도 발생함. 헉!
        const toUpload = tableData.filter((row)=>{
            if(row && row.ok) return true;
            else return false;
        }).map((row)=>InputUtil.getDataToSubmit(row, uploadMap, false));
        const param = {variables: {autoDidaInfoList: toUpload}};

        uploadAutoDidaInfo(param);
    };

    const onClickFindFile = () => {
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const onCsvFileRead = (content, isEucKr) => {
        setUseEucKr(isEucKr);
        setText(content);
    };

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                InputUtil.readTextFileWithAutoEncoding(file, onCsvFileRead);
            }
        }
    };

    const alertErrorData = (row) => {
        const lines = row.message.split("\n").filter((line)=>line.length > 0);
        addAlert({title: `점포 휴무일 정보 오류(${row.dcCd}-${row.storeId})`, message: lines});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case 'error': alertErrorData(row); break;
            default: break;
        }
    };

    const canAdd = checkAuth(authTableTree.tree.pr_auto_dida_info_add); // 별도 upload 프로시져 없이 ADD procedure로 처리.

    const tools = [
        {id:'error', icon: <NotificationImportantIcon/>, color: AppStyle.mui.error, where: (row) => !row.ok}
    ];

    const onClickCancel = () => {
        onClose(wellUploadCountRef.current);
    };

    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClickCancel}
            startIcon={<CloudUploadIcon size={AppWord.LARGE} />}
            title="점포 휴무일 정보 업로드"
        >
            <DialogContent style={{position: 'relative', minHeight:'50vh', minWidth:600, width:'50vw'}}>
                <Box style={{position:'absolute', left:5, top:0, right:5, bottom:3, display:'flex', flexDirection: 'column'}}>
                    <HeaderLine>
                        <div style={{flexGrow:1, display:'flex', alignItems:'center'}}>
                        {msgDone && <ThumbUpAltIcon />}
                        {
                            tableData.length < 1 || !changed ?
                            <span>{msgDone}데이터 파일(CSV)을 선택해 주세요</span>
                            :
                            errorCount === 0 ?
                            <span>모든 데이터가 정상입니다. 업로드 버튼을 눌러주세요</span>
                            :
                            errorCount === tableData.length ?
                            <span>모든 데이터가 오류입니다. 수정해 주세요 (오류는 각 줄 끝 아이콘 눌러 확인)</span>
                            :
                            <span>오류가 없는 데이터만 올리려면 업로드 버튼을 눌러주세요 (오류는 각 줄 끝 아이콘 눌러 확인)</span>
                        }
                        </div>
                    </HeaderLine>
                    <FlexyTable
                        name={tableProps.tableId}
                        tableProps={tableProps}
                        records={tableData}
                        tools={tools}
                        onClickTool={onClickToolOnRow}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={onChooseFile}
                />
                {
                    fileInputRef.current && canAdd &&
                    <>
                    {
                        useEucKr ?
                        <Chip label="완성형한글로 읽음" color={AppStyle.mui.warning}
                            size={AppWord.SMALL}
                            style={{marginRight:10}}
                        />
                        :
                        tableData.length > 0 ?
                        <Chip label="UTF-8로 읽음" color={AppStyle.mui.success}
                            size={AppWord.SMALL}
                            style={{marginRight:10}}
                        /> : null
                    }
                    <Button
                        color={AppStyle.mui.secondary}
                        style={{marginRight:10}}
                        onClick={onClickFindFile}
                        startIcon={<FindInPageIcon />}
                        variant={AppStyle.mui.contained}
                    >
                        다른 파일 선택
                    </Button>
                    </>
                }
                {
                    canAdd &&
                    <Button
                        color={AppStyle.mui.primary}
                        style={{marginRight:10}}
                        startIcon={<CloudUploadIcon />}
                        onClick={onClickUpload}
                        variant={AppStyle.mui.contained}
                        disabled={
                            (errorCount > 0 && errorCount === tableData.length) || !changed
                            || responseUpload.loading
                        }
                    >
                        데이터 업로드
                    </Button>
                }
                <Button onClick={onClickCancel} color={AppStyle.mui.primary}
                    variant={AppStyle.mui.contained}
                    startIcon={<DoneIcon />}
                    disabled={responseUpload.loading}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}